import React, { useState } from 'react';
import { toast } from 'sonner';
import { User, Camera, ArrowLeft, Save, MapPin, Phone, Plus, Trash2, Star, Edit2, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getErrorMessage } from '../utils/errorHandler';
import { compressImage } from '../utils/imageCompressor';

const UserProfile = () => {
  const { user, getAuthHeaders, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [newAddress, setNewAddress] = useState({ label: '', address: '', isDefault: false });
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [editingAddressData, setEditingAddressData] = useState({ label: '', address: '', isDefault: false });

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    // Comprimir en el navegador (redimension a 400x400 + WebP): se acepta
    // cualquier imagen sin tope de tamano porque llega ya optimizada al backend.
    const compressedFile = await compressImage(file, { maxSize: 400 });
    setAvatarFile(compressedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(compressedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    // Validate password change if requested
    if (changingPassword) {
      if (!formData.current_password) {
        toast.error('Ingresa tu contraseña actual');
        return;
      }
      if (!formData.new_password || formData.new_password.length < 6) {
        toast.error('La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
    }

    setLoading(true);
    try {
      let avatarUrl = user?.avatar_url;
      
      // Upload avatar if changed
      if (avatarFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', avatarFile);
        
        const uploadResponse = await axios.post(
          `${API}/users/avatar`,
          formDataUpload,
          {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        avatarUrl = uploadResponse.data.avatar_url;
      }

      // Update user profile
      const response = await axios.patch(
        `${API}/users/profile`,
        {
          name: formData.name,
          phone: formData.phone,
          addresses: addresses,
          avatar_url: avatarUrl,
          ...(changingPassword && {
            current_password: formData.current_password,
            new_password: formData.new_password
          })
        },
        {
          headers: getAuthHeaders()
        }
      );

      // Update context
      updateUser(response.data);
      
      toast.success('Perfil actualizado exitosamente');
      
      // Reset password fields
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      setChangingPassword(false);
      
      navigate(-1); // Go back to previous page
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(getErrorMessage(error, 'Error al actualizar perfil'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="rounded-full w-10 h-10 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Mi Perfil</h1>
            <p className="text-gray-600">Gestiona tu información personal</p>
          </div>
        </div>

        <Card className="p-8 bg-white/80 backdrop-blur-xl border-orange-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center overflow-hidden">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-white" />
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50 border-2 border-orange-200">
                  <Camera className="w-5 h-5 text-orange-500" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500">
                Click en la cámara para cambiar tu foto
              </p>
            </div>

            {/* Name Field */}
            <div>
              <Label htmlFor="name">Nombre Completo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Tu nombre"
                className="rounded-xl"
                required
              />
            </div>

            {/* Email Field (Read-only) */}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={formData.email}
                disabled
                className="rounded-xl bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                El email no puede ser modificado
              </p>
            </div>

            {/* Phone Field */}
            <div>
              <Label htmlFor="phone">
                <Phone className="w-4 h-4 inline mr-1" />
                Teléfono
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Tu número de teléfono"
                className="rounded-xl"
              />
            </div>

            {/* Role Badge - Only show for staff roles */}
            {user?.role && user?.role !== 'customer' && (
              <div>
                <Label>Rol</Label>
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    {user?.role === 'admin' && 'Administrador'}
                    {user?.role === 'waiter' && 'Mesero'}
                    {user?.role === 'cashier' && 'Cajero'}
                    {user?.role === 'kitchen' && 'Cocina'}
                    {user?.role === 'super_admin' && 'Super Administrador'}
                    {user?.role === 'delivery' && 'Delivery'}
                    {user?.role === 'customer' && 'Cliente'}
                  </span>
                </div>
              </div>
            )}

            {/* Addresses Section */}
            <div className="border-t pt-6 mt-6">
              <Label className="text-lg font-semibold flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5" />
                Mis Direcciones
              </Label>
              
              {/* Existing Addresses */}
              {addresses.length > 0 && (
                <div className="space-y-3 mb-4">
                  {addresses.map((addr, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-xl">
                      {editingAddressIndex === index ? (
                        /* Edit Mode */
                        <div className="space-y-3">
                          <Input
                            placeholder="Etiqueta (ej: Casa, Trabajo)"
                            value={editingAddressData.label}
                            onChange={(e) => setEditingAddressData({ ...editingAddressData, label: e.target.value })}
                            className="rounded-xl"
                          />
                          <Input
                            placeholder="Dirección completa"
                            value={editingAddressData.address}
                            onChange={(e) => setEditingAddressData({ ...editingAddressData, address: e.target.value })}
                            className="rounded-xl"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`edit-isDefault-${index}`}
                              checked={editingAddressData.isDefault}
                              onChange={(e) => setEditingAddressData({ ...editingAddressData, isDefault: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            <Label htmlFor={`edit-isDefault-${index}`} className="text-sm cursor-pointer">
                              Dirección predeterminada
                            </Label>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                if (!editingAddressData.address.trim()) {
                                  toast.error('Ingresa una dirección');
                                  return;
                                }
                                const updatedAddresses = addresses.map((a, i) => {
                                  if (i === index) {
                                    return { ...editingAddressData };
                                  }
                                  if (editingAddressData.isDefault) {
                                    return { ...a, isDefault: false };
                                  }
                                  return a;
                                });
                                setAddresses(updatedAddresses);
                                setEditingAddressIndex(null);
                                toast.success('Dirección actualizada');
                              }}
                              className="flex-1 rounded-xl bg-green-500 hover:bg-green-600"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Guardar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingAddressIndex(null)}
                              className="flex-1 rounded-xl"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{addr.label || 'Dirección'}</span>
                              {addr.isDefault && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                  <Star className="w-3 h-3 mr-1" />
                                  Predeterminada
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{addr.address}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingAddressIndex(index);
                                setEditingAddressData({ ...addr });
                              }}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newAddresses = addresses.filter((_, i) => i !== index);
                                setAddresses(newAddresses);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Address */}
              <div className="space-y-3 p-4 bg-orange-50 rounded-xl border border-orange-100">
                <p className="text-sm font-medium text-gray-700">Agregar nueva dirección</p>
                <Input
                  placeholder="Etiqueta (ej: Casa, Trabajo)"
                  value={newAddress.label}
                  onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                  className="rounded-xl"
                />
                <Input
                  placeholder="Dirección completa"
                  value={newAddress.address}
                  onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                  className="rounded-xl"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={newAddress.isDefault}
                    onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="isDefault" className="text-sm cursor-pointer">
                    Establecer como predeterminada
                  </Label>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (!newAddress.address.trim()) {
                      toast.error('Ingresa una dirección');
                      return;
                    }
                    const updatedAddresses = newAddress.isDefault 
                      ? addresses.map(a => ({ ...a, isDefault: false }))
                      : [...addresses];
                    setAddresses([...updatedAddresses, { ...newAddress }]);
                    setNewAddress({ label: '', address: '', isDefault: false });
                    toast.success('Dirección agregada');
                  }}
                  className="w-full rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Dirección
                </Button>
              </div>
            </div>

            {/* Password Change Section */}
            <div className="border-t pt-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-lg font-semibold">Cambiar Contraseña</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setChangingPassword(!changingPassword)}
                  className="rounded-xl"
                >
                  {changingPassword ? 'Cancelar' : 'Cambiar'}
                </Button>
              </div>

              {changingPassword && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Contraseña Actual *</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={formData.current_password}
                      onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                      placeholder="Ingresa tu contraseña actual"
                      className="rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new_password">Nueva Contraseña *</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      placeholder="Mínimo 6 caracteres"
                      className="rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirmar Nueva Contraseña *</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      placeholder="Repite la nueva contraseña"
                      className="rounded-xl"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                {loading ? (
                  'Guardando...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default UserProfile;
